import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { usabilityAdminService } from "../../services";
import HeaderTailwind from "../generic/HeaderTailwind";
import ConfirmationModal from "./ConfirmationModal";
import GenericSelectTailwind from "./GenericSelectTailwind";

function UsabilityEvaluationsTailwind() {
  const { t } = useTranslation();
  const [sessions, setSessions] = useState([]);
  const [aggregate, setAggregate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSession, setSelectedSession] = useState(null);
  
  // Filter States
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRole, setSelectedRole] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  
  // Pagination States
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  // Confirmation Modal States
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState(null);
  const [showMarkCorrectDialog, setShowMarkCorrectDialog] = useState(false);
  const [taskResultToMark, setTaskResultToMark] = useState(null);
  
  // Multi-Select States
  const [selectedSessionIds, setSelectedSessionIds] = useState(new Set());
  const [isSelectMode, setIsSelectMode] = useState(false);
  
  // Import States
  const [showImportConfirmation, setShowImportConfirmation] = useState(false);
  const [importData, setImportData] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  
  // Toast State
  const [toast, setToast] = useState({ show: false, message: "", type: "info" });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [sessionsResponse, aggregateResponse] = await Promise.all([
        usabilityAdminService.getAllSessions(),
        usabilityAdminService.getAggregateEvaluation()
      ]);

      if (sessionsResponse.success) {
        const sessionsData = Array.isArray(sessionsResponse.data) ? sessionsResponse.data : [];
        setSessions(sessionsData);
      } else {
        setError(sessionsResponse.error || "Fehler beim Laden der Sessions");
        setSessions([]);
      }
      if (aggregateResponse.success) {
        setAggregate(aggregateResponse.data);
      }
    } catch (err) {
      setError(err.message || "Fehler beim Laden der Daten");
      setSessions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleViewSession = async (sessionId) => {
    try {
      const response = await usabilityAdminService.getSessionDetail(sessionId);
      if (response.success) {
        setSelectedSession(response.data);
      } else {
        setError(response.error || "Fehler beim Laden der Session-Details");
      }
    } catch (err) {
      setError(err.message || "Fehler beim Laden der Session-Details");
    }
  };

  const handleDeleteSession = (session) => {
    setSessionToDelete(session);
    setShowConfirmation(true);
  };

  const handleDeleteSelectedSessions = () => {
    if (selectedSessionIds.size === 0) return;
    setShowConfirmation(true);
  };

  const handleToggleSelectMode = () => {
    setIsSelectMode(!isSelectMode);
    if (isSelectMode) {
      setSelectedSessionIds(new Set());
    }
  };

  const handleToggleSessionSelection = (sessionId) => {
    setSelectedSessionIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sessionId)) {
        newSet.delete(sessionId);
      } else {
        newSet.add(sessionId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedSessionIds.size === paginatedSessions.length) {
      setSelectedSessionIds(new Set());
    } else {
      setSelectedSessionIds(new Set(paginatedSessions.map(s => s.id)));
    }
  };

  const addToast = (type, message) => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: "", type: "info" }), 5000);
  };

  const hideToast = () => {
    setToast({ show: false, message: "", type: "info" });
  };

  const handleConfirmDelete = async () => {
    // Wenn mehrere Sessions ausgewählt sind, lösche alle
    if (selectedSessionIds.size > 0) {
      try {
        const deletePromises = Array.from(selectedSessionIds).map(sessionId =>
          usabilityAdminService.deleteSession(sessionId)
        );
        
        const results = await Promise.all(deletePromises);
        const allSuccess = results.every(r => r.success);
        
        if (allSuccess) {
          await loadData();
          setShowConfirmation(false);
          const count = selectedSessionIds.size;
          setSelectedSessionIds(new Set());
          setIsSelectMode(false);
          addToast("success", `${count} ${count === 1 ? 'Session wurde' : 'Sessions wurden'} erfolgreich gelöscht`);
        } else {
          const failedCount = results.filter(r => !r.success).length;
          setError(`${failedCount} Session(s) konnten nicht gelöscht werden`);
          addToast("error", `${failedCount} Session(s) konnten nicht gelöscht werden`);
        }
      } catch (err) {
        setError('Fehler beim Löschen der Sessions');
        console.error("Fehler beim Löschen:", err);
      }
    } else if (sessionToDelete) {
      // Einzelne Session löschen (bestehende Logik)
      try {
        const response = await usabilityAdminService.deleteSession(sessionToDelete.id);
        
        if (response.success) {
          await loadData();
          setShowConfirmation(false);
          setSessionToDelete(null);
          addToast("success", "Session wurde erfolgreich gelöscht");
        } else {
          setError(response.error || 'Fehler beim Löschen der Session');
          addToast("error", response.error || 'Fehler beim Löschen der Session');
        }
      } catch (err) {
        setError('Fehler beim Löschen der Session');
        console.error("Fehler beim Löschen:", err);
      }
    }
  };

  const handleCloseConfirmation = () => {
    setShowConfirmation(false);
    setSessionToDelete(null);
    // Auswahl nicht zurücksetzen, falls der Benutzer nur abbricht
  };

  const handleMarkAsCorrect = (taskResultId) => {
    const taskResult = selectedSession.taskResults.find(r => r.id === taskResultId);
    if (taskResult) {
      setTaskResultToMark(taskResult);
      setShowMarkCorrectDialog(true);
    }
  };

  const handleConfirmMarkAsCorrect = async () => {
    if (!taskResultToMark) return;

    try {
      const response = await usabilityAdminService.markTaskResultAsCorrect(taskResultToMark.id);
      
      if (response.success) {
        // Aktualisiere die Session-Details
        await handleViewSession(selectedSession.session.id);
        setShowMarkCorrectDialog(false);
        setTaskResultToMark(null);
      } else {
        setError(response.error || 'Fehler beim Markieren als korrekt');
      }
    } catch (err) {
      setError('Fehler beim Markieren als korrekt');
      console.error("Fehler beim Markieren:", err);
    }
  };

  const handleCloseMarkCorrectDialog = () => {
    setShowMarkCorrectDialog(false);
    setTaskResultToMark(null);
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleResetFilters = () => {
    setSearchTerm("");
    setSelectedRole("all");
    setSelectedStatus("all");
  };

  // Get active filters for display
  const getActiveFilters = () => {
    const active = [];
    if (selectedRole !== "all") {
      const roleLabel = roleOptions.find(r => r.value === selectedRole)?.label || selectedRole;
      active.push({ type: "Rolle", value: roleLabel, onRemove: () => setSelectedRole("all") });
    }
    if (selectedStatus !== "all") {
      const statusLabel = statusOptions.find(s => s.value === selectedStatus)?.label || selectedStatus;
      active.push({ type: "Status", value: statusLabel, onRemove: () => setSelectedStatus("all") });
    }
    if (searchTerm.trim() !== "") {
      active.push({ type: "Suche", value: searchTerm, onRemove: () => setSearchTerm("") });
    }
    return active;
  };

  // Filter options
  const roleOptions = [
    { value: "all", label: t("admin.usability.evaluations.filters.allRoles", "Alle Rollen") },
    { value: "admin", label: "Admin" },
    { value: "scorekeeper", label: "Scorekeeper" },
    { value: "caller", label: "Caller" },
    { value: "superadmin", label: "Superadmin" },
    { value: "user", label: "User (Zuschauer)" }
  ];

  const statusOptions = [
    { value: "all", label: t("admin.usability.evaluations.filters.allStatuses", "Alle Status") },
    { value: "completed", label: t("admin.usability.evaluations.completed", "Abgeschlossen") },
    { value: "in_progress", label: t("admin.usability.evaluations.inProgress", "In Bearbeitung") },
    { value: "aborted", label: t("admin.usability.evaluations.abandoned", "Abgebrochen") }
  ];

  // Filter- und Suchlogik
  const filteredSessions = sessions.filter((session) => {
    // Suche nach Session ID oder User (case-insensitive)
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = 
      !searchTerm.trim() ||
      String(session.id).includes(searchLower) ||
      (session.user?.username || "").toLowerCase().includes(searchLower) ||
      (session.participantCode || "").toLowerCase().includes(searchLower);

    // Rolle-Filter
    const matchesRole = selectedRole === "all" || session.roleName === selectedRole;

    // Status-Filter
    const matchesStatus = selectedStatus === "all" || session.status === selectedStatus;

    return matchesSearch && matchesRole && matchesStatus;
  });

  // Pagination Logic
  const totalItems = filteredSessions.length;
  const itemsPerPageNum = itemsPerPage === "all" ? totalItems : (typeof itemsPerPage === "string" ? parseInt(itemsPerPage) : itemsPerPage);
  const totalPages = itemsPerPage === "all" ? 1 : Math.ceil(totalItems / itemsPerPageNum);
  const startIndex = itemsPerPage === "all" ? 0 : (currentPage - 1) * itemsPerPageNum;
  const endIndex = itemsPerPage === "all" ? totalItems : startIndex + itemsPerPageNum;
  const paginatedSessions = itemsPerPage === "all" 
    ? filteredSessions 
    : filteredSessions.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedRole, selectedStatus, itemsPerPage]);

  // Scroll to top when page changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(value === "all" ? "all" : parseInt(value));
    setCurrentPage(1);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Export: Alle (gefilterte) Sessions mit Details als JSON exportieren
  const handleExport = async () => {
    try {
      setIsExporting(true);
      
      // Für jede Session die Details laden
      const sessionsToExport = filteredSessions;
      const detailedSessions = [];

      for (const session of sessionsToExport) {
        try {
          const detailResponse = await usabilityAdminService.getSessionDetail(session.id);
          if (detailResponse.success && detailResponse.data) {
            const detail = detailResponse.data;
            detailedSessions.push({
              id: session.id,
              participantCode: session.participantCode,
              user: session.user ? { username: session.user.username } : null,
              roleName: session.roleName,
              status: session.status,
              startedAt: session.startedAt,
              completedAt: session.completedAt,
              stats: session.stats,
              taskResults: (detail.taskResults || []).map(r => ({
                taskId: r.taskId,
                task: r.task ? { title: r.task.title, description: r.task.description, expectedType: r.task.expectedType, expectedInputValue: r.task.expectedInputValue } : null,
                startedAt: r.startedAt,
                completedAt: r.completedAt,
                durationSeconds: r.durationSeconds,
                isCorrect: r.isCorrect,
                actualInputValue: r.actualInputValue,
                actualStateSnapshot: r.actualStateSnapshot
              })),
              susAnswers: (detail.susAnswers || []).map(a => ({
                questionIndex: a.questionIndex,
                score: a.score
              })),
              susScore: detail.susScore,
              openAnswers: (detail.openAnswers || []).map(a => ({
                openQuestionId: a.openQuestionId,
                questionText: a.openQuestion?.questionText || null,
                answerText: a.answerText
              }))
            });
          }
        } catch (detailErr) {
          console.error(`Fehler beim Laden der Details für Session ${session.id}:`, detailErr);
        }
      }

      const exportData = {
        version: "1.0",
        exportDate: new Date().toISOString(),
        totalSessions: detailedSessions.length,
        sessions: detailedSessions
      };

      const dataStr = JSON.stringify(exportData, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `usability-evaluations-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      addToast("success", `${detailedSessions.length} Session(s) erfolgreich exportiert`);
    } catch (err) {
      addToast("error", "Fehler beim Exportieren der Auswertungen");
      console.error("Export error:", err);
    } finally {
      setIsExporting(false);
    }
  };

  // Import: JSON-Datei einlesen
  const handleImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      const text = await file.text();
      const parsed = JSON.parse(text);

      if (!parsed.sessions || !Array.isArray(parsed.sessions)) {
        addToast("error", "Ungültiges Dateiformat: sessions-Array nicht gefunden");
        return;
      }

      setImportData(parsed.sessions);
      setShowImportConfirmation(true);
    } catch (err) {
      addToast("error", "Fehler beim Lesen der Datei");
      console.error("Import error:", err);
    } finally {
      event.target.value = '';
    }
  };

  // Import bestätigen
  const handleConfirmImport = async () => {
    if (!importData) return;

    try {
      setLoading(true);
      const response = await usabilityAdminService.importSessions(importData);

      if (response.success) {
        await loadData();
        const data = response.data?.data || response.data;
        addToast("success", data?.message || `${importData.length} Session(s) erfolgreich importiert`);
      } else {
        addToast("error", response.error || "Fehler beim Importieren");
      }
    } catch (err) {
      addToast("error", "Fehler beim Importieren der Sessions");
      console.error("Import error:", err);
    } finally {
      setLoading(false);
      setShowImportConfirmation(false);
      setImportData(null);
    }
  };

  // Berechne KPIs für abgeschlossene Tests
  const calculateCompletedKPIs = () => {
    const completedSessions = sessions.filter(s => s.status === 'completed');
    const completedCount = completedSessions.length;

    if (completedCount === 0) {
      return {
        count: 0,
        avgSuccessRate: 0,
        avgDuration: 0,
        avgSusScore: 0
      };
    }

    // Durchschnittliche Erfolgsquote
    const totalSuccessRate = completedSessions.reduce((sum, s) => sum + (s.stats?.successRate || 0), 0);
    const avgSuccessRate = Math.round(totalSuccessRate / completedCount);

    // Durchschnittliche Zeit (in Sekunden)
    const totalDuration = completedSessions.reduce((sum, s) => sum + (s.stats?.totalDurationSeconds || 0), 0);
    const avgDurationSeconds = Math.round(totalDuration / completedCount);

    // Durchschnittlicher SUS-Score
    const totalSusScore = completedSessions.reduce((sum, s) => {
      const score = s.stats?.susScore ? parseFloat(s.stats.susScore) : 0;
      return sum + score;
    }, 0);
    const avgSusScore = totalSusScore / completedCount;

    return {
      count: completedCount,
      avgSuccessRate,
      avgDuration: avgDurationSeconds,
      avgSusScore: avgSusScore || 0
    };
  };

  const completedKPIs = calculateCompletedKPIs();

  // Formatiere Zeit für Anzeige
  const formatDuration = (seconds) => {
    if (!seconds || seconds === 0) return "0:00";
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${String(secs).padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 transition-colors">
        <HeaderTailwind />
        <main className="pt-24 pb-8 bg-slate-50/50 dark:bg-slate-900 min-h-screen transition-colors">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-center min-h-[400px]">
              <div className="text-center">
                <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-slate-600 dark:text-slate-400">Lade Daten...</p>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (error && sessions.length === 0) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 transition-colors">
        <HeaderTailwind />
        <main className="pt-24 pb-8 bg-slate-50/50 dark:bg-slate-900 min-h-screen transition-colors">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-center min-h-[400px]">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full mb-4">
                  <i className="fas fa-exclamation-triangle text-red-600 dark:text-red-400"></i>
                </div>
                <p className="text-slate-900 dark:text-slate-100 font-medium mb-4">{error}</p>
                <button 
                  onClick={loadData} 
                  className="inline-flex items-center px-4 py-2 bg-primary dark:bg-primary-light text-white text-sm font-medium rounded-lg hover:bg-primary-dark dark:hover:bg-primary transition-colors"
                >
                  <i className="fas fa-redo mr-2"></i>
                  Erneut versuchen
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 transition-colors">
      <HeaderTailwind />
      <main className="pt-24 pb-8 bg-slate-50/50 dark:bg-slate-900 min-h-screen transition-colors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header Section */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
                  {t("admin.usability.evaluations.title", "Usability-Test Auswertungen")}
                </h1>
                <p className="text-slate-600 dark:text-slate-400 mt-2">
                  {t("admin.usability.evaluations.subtitle", "Übersicht über alle Usability-Test Sessions und Statistiken.")}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <div className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                    {filteredSessions.length}
                  </div>
                  <div className="text-sm text-slate-500 dark:text-slate-400">Sessions</div>
                </div>
              </div>
            </div>

            {/* Export / Import Buttons */}
            <div className="mt-4 flex items-center justify-end gap-2">
              <button
                onClick={handleExport}
                disabled={isExporting || filteredSessions.length === 0}
                className="inline-flex items-center gap-2 px-4 py-2 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 text-sm font-medium rounded-lg border border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isExporting ? (
                  <>
                    <div className="w-3.5 h-3.5 border-2 border-slate-400 border-t-transparent rounded-full animate-spin"></div>
                    Exportiere...
                  </>
                ) : (
                  <>
                    <i className="fas fa-download text-xs"></i>
                    Exportieren
                  </>
                )}
              </button>
              <label className="inline-flex items-center gap-2 px-4 py-2 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 text-sm font-medium rounded-lg border border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20 cursor-pointer">
                <i className="fas fa-upload text-xs"></i>
                Importieren
                <input
                  type="file"
                  accept=".json"
                  onChange={handleImport}
                  className="hidden"
                />
              </label>
            </div>
          </div>

          {/* KPI Section für abgeschlossene Tests */}
          <div className="mb-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Anzahl abgeschlossener Tests */}
            <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm p-6 flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  {t("admin.usability.evaluations.kpi.completedTests", "Abgeschlossene Tests")}
                </p>
                <p className="mt-2 text-3xl font-bold text-slate-900 dark:text-slate-100">
                  {completedKPIs.count}
                </p>
              </div>
              <div className="w-12 h-12 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center text-emerald-600 dark:text-emerald-400">
                <i className="fas fa-check-circle text-lg"></i>
              </div>
            </div>

            {/* Durchschnittliche Erfolgsquote */}
            <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm p-6 flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  {t("admin.usability.evaluations.kpi.avgSuccessRate", "Ø Erfolgsquote")}
                </p>
                <p className="mt-2 text-3xl font-bold text-slate-900 dark:text-slate-100">
                  {completedKPIs.avgSuccessRate}%
                </p>
              </div>
              <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400">
                <i className="fas fa-percentage text-lg"></i>
              </div>
            </div>

            {/* Durchschnittliche Zeit */}
            <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm p-6 flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  {t("admin.usability.evaluations.kpi.avgDuration", "Ø Zeit")}
                </p>
                <p className="mt-2 text-3xl font-bold text-slate-900 dark:text-slate-100">
                  {formatDuration(completedKPIs.avgDuration)}
                </p>
              </div>
              <div className="w-12 h-12 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center text-purple-600 dark:text-purple-400">
                <i className="fas fa-clock text-lg"></i>
              </div>
            </div>

            {/* Durchschnittlicher SUS-Score */}
            <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm p-6 flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  {t("admin.usability.evaluations.kpi.avgSusScore", "Ø SUS-Score")}
                </p>
                <p className="mt-2 text-3xl font-bold text-slate-900 dark:text-slate-100">
                  {completedKPIs.avgSusScore > 0 ? parseFloat(completedKPIs.avgSusScore).toFixed(1) : "0.0"}
                </p>
              </div>
              <div className="w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center text-amber-600 dark:text-amber-400">
                <i className="fas fa-star text-lg"></i>
              </div>
            </div>
          </div>

          {/* Filter Section */}
          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm p-6 mb-6">
            {/* Search Bar */}
            <div className="mb-6">
              <div className="relative">
                <input
                  type="text"
                  placeholder={t("admin.usability.evaluations.searchPlaceholder", "Nach Session-ID oder User suchen...")}
                  value={searchTerm}
                  onChange={handleSearch}
                  className="w-full pl-10 pr-4 h-[46px] border border-slate-200 dark:border-slate-600 rounded-xl text-sm text-slate-900 dark:text-slate-100 bg-slate-50 dark:bg-slate-700 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light"
                />
                <i className="fas fa-search absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 dark:text-slate-500 text-sm"></i>
              </div>
            </div>

            {/* Filter Dropdowns */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {/* Role Filter */}
              <div>
                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide mb-2">
                  {t("admin.usability.evaluations.role", "Rolle")}
                </label>
                <GenericSelectTailwind
                  value={selectedRole}
                  onChange={setSelectedRole}
                  options={roleOptions.map(opt => ({
                    value: opt.value,
                    label: opt.label
                  }))}
                  placeholder="Rolle auswählen..."
                />
              </div>

              {/* Status Filter */}
              <div>
                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide mb-2">
                  {t("admin.usability.evaluations.status", "Status")}
                </label>
                <GenericSelectTailwind
                  value={selectedStatus}
                  onChange={setSelectedStatus}
                  options={statusOptions.map(opt => ({
                    value: opt.value,
                    label: opt.label
                  }))}
                  placeholder="Status auswählen..."
                />
              </div>
            </div>

            {/* Active Filters Section */}
            {getActiveFilters().length > 0 && (
              <>
                <div className="border-t border-slate-200 dark:border-slate-700 pt-4 mt-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300">
                      Aktive Filter:
                    </h3>
                    <button
                      onClick={handleResetFilters}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 text-sm font-medium rounded-lg border border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                    >
                      <i className="fas fa-undo text-xs"></i>
                      Filter zurücksetzen
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {getActiveFilters().map((filter, index) => (
                      <div
                        key={index}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800"
                      >
                        <span>{filter.type}: {filter.value}</span>
                        <button
                          onClick={filter.onRemove}
                          className="ml-1 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 transition-colors"
                        >
                          <i className="fas fa-times text-[10px]"></i>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* Mehrfachauswahl Button */}
            <div className="border-t border-slate-200 dark:border-slate-700 pt-4 mt-4">
              <button
                onClick={handleToggleSelectMode}
                className="inline-flex items-center gap-2 px-4 py-2 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 text-sm font-medium rounded-lg border border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
              >
                <i className={`fas ${isSelectMode ? 'fa-times' : 'fa-check-square'} text-xs`}></i>
                {isSelectMode ? 'Auswahl beenden' : 'Mehrere auswählen'}
              </button>
            </div>
          </div>

          {/* Sessions-Liste */}
          {filteredSessions.length > 0 && (
            <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
              {/* Toolbar für Mehrfachauswahl */}
              {isSelectMode && (
                <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-700/50 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                      {selectedSessionIds.size} {selectedSessionIds.size === 1 ? 'Session ausgewählt' : 'Sessions ausgewählt'}
                    </span>
                    <button
                      onClick={handleSelectAll}
                      className="text-sm text-primary dark:text-primary-light hover:underline"
                    >
                      {paginatedSessions.length > 0 && selectedSessionIds.size === paginatedSessions.length ? 'Alle abwählen' : 'Alle auswählen'}
                    </button>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handleToggleSelectMode}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 text-sm font-medium rounded-lg border border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                    >
                      <i className="fas fa-times text-xs"></i>
                      Auswahl beenden
                    </button>
                    <button
                      onClick={handleDeleteSelectedSessions}
                      disabled={selectedSessionIds.size === 0}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-red-500 dark:bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-600 dark:hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <i className="fas fa-trash text-xs"></i>
                      {selectedSessionIds.size} {selectedSessionIds.size === 1 ? 'Session löschen' : 'Sessions löschen'}
                    </button>
                  </div>
                </div>
              )}
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-700">
                  <thead className="border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-700/50">
                    <tr>
                      {isSelectMode && (
                        <th className="px-6 py-3 text-left">
                          <div className="relative">
                            <input
                              type="checkbox"
                              checked={paginatedSessions.length > 0 && selectedSessionIds.size === paginatedSessions.length}
                              onChange={handleSelectAll}
                              className="sr-only"
                            />
                            <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition-all ${
                              paginatedSessions.length > 0 && selectedSessionIds.size === paginatedSessions.length
                                ? 'bg-primary dark:bg-primary-light border-primary dark:border-primary-light'
                                : 'bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600'
                            } cursor-pointer`}>
                              {paginatedSessions.length > 0 && selectedSessionIds.size === paginatedSessions.length && (
                                <i className="fas fa-check text-white text-[10px]"></i>
                              )}
                            </div>
                          </div>
                        </th>
                      )}
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-300 uppercase tracking-wider">
                        {t("admin.usability.evaluations.sessionId", "Session-ID")}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-300 uppercase tracking-wider">
                        {t("admin.usability.evaluations.user", "User")}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-300 uppercase tracking-wider">
                        {t("admin.usability.evaluations.role", "Rolle")}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-300 uppercase tracking-wider">
                        {t("admin.usability.evaluations.status", "Status")}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-300 uppercase tracking-wider">
                        {t("admin.usability.evaluations.successRate", "Erfolgsquote")}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-300 uppercase tracking-wider">
                        {t("admin.usability.evaluations.totalDuration", "Gesamtzeit")}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-300 uppercase tracking-wider">
                        {t("admin.usability.evaluations.susScore", "SUS-Score")}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-300 uppercase tracking-wider">
                        {t("admin.usability.evaluations.actions", "Aktionen")}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-slate-800 divide-y divide-slate-200 dark:divide-slate-700">
                    {paginatedSessions.map((session) => (
                      <tr key={session.id} className={`hover:bg-slate-50/50 dark:hover:bg-slate-700/50 transition-colors ${isSelectMode && selectedSessionIds.has(session.id) ? 'bg-blue-50/50 dark:bg-blue-900/20' : ''}`}>
                        {isSelectMode && (
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="relative">
                              <input
                                type="checkbox"
                                checked={selectedSessionIds.has(session.id)}
                                onChange={() => handleToggleSessionSelection(session.id)}
                                className="sr-only"
                                id={`checkbox-${session.id}`}
                              />
                              <div 
                                onClick={() => handleToggleSessionSelection(session.id)}
                                className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition-all ${
                                  selectedSessionIds.has(session.id)
                                    ? 'bg-primary dark:bg-primary-light border-primary dark:border-primary-light'
                                    : 'bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600'
                                } cursor-pointer`}
                              >
                                {selectedSessionIds.has(session.id) && (
                                  <i className="fas fa-check text-white text-[10px]"></i>
                                )}
                              </div>
                            </div>
                          </td>
                        )}
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-slate-600 dark:text-slate-400">
                          #{session.id}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900 dark:text-slate-50">
                          {session.user?.username || session.participantCode || "Anonym"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500 dark:text-slate-300">
                          {session.roleName}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                            session.status === "completed"
                              ? "bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 border border-green-700/30 dark:border-green-300/30"
                              : session.status === "in_progress"
                              ? "bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200 border border-yellow-700/30 dark:border-yellow-300/30"
                              : "bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 border border-red-700/30 dark:border-red-300/30"
                          }`}>
                            {session.status === "completed" ? t("admin.usability.evaluations.completed", "Abgeschlossen") :
                             session.status === "in_progress" ? t("admin.usability.evaluations.inProgress", "In Bearbeitung") :
                             t("admin.usability.evaluations.abandoned", "Abgebrochen")}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500 dark:text-slate-300">
                          {session.stats?.successRate || 0}%
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500 dark:text-slate-300">
                          {(() => {
                            const totalSeconds = session.stats?.totalDurationSeconds || 0;
                            const minutes = Math.floor(totalSeconds / 60);
                            const seconds = totalSeconds % 60;
                            return totalSeconds > 0 ? `${minutes}:${String(seconds).padStart(2, '0')}` : "N/A";
                          })()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500 dark:text-slate-300">
                          {session.stats?.susScore ? parseFloat(session.stats.susScore).toFixed(1) : "N/A"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => handleViewSession(session.id)}
                              className="p-2 text-slate-600 dark:text-slate-400 hover:text-primary dark:hover:text-primary-light transition-colors"
                              title={t("admin.usability.evaluations.details", "Details")}
                            >
                              <i className="fas fa-eye text-xs"></i>
                            </button>
                            <button
                              onClick={() => handleDeleteSession(session)}
                              className="p-2 text-slate-600 dark:text-slate-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                              title={t("admin.usability.evaluations.delete", "Löschen")}
                            >
                              <i className="fas fa-trash text-xs"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    
                    {/* Pagination Footer Row - nur anzeigen wenn kein Modal geöffnet ist */}
                    {!selectedSession && (
                      <tr className="bg-slate-50 dark:bg-slate-700/30">
                        <td colSpan={8} className="px-6 py-4">
                          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                            {/* Items per page selector */}
                            <div className="flex items-center gap-3">
                              <span className="text-sm text-slate-600 dark:text-slate-400">
                                Zeige
                              </span>
                              <div className="relative" style={{ zIndex: showConfirmation || selectedSession ? 1 : 100, pointerEvents: showConfirmation || selectedSession ? 'none' : 'auto' }}>
                                <GenericSelectTailwind
                                  value={itemsPerPage.toString()}
                                  onChange={(value) => handleItemsPerPageChange(value === "all" ? "all" : parseInt(value))}
                                  options={[
                                    { value: "10", label: "10" },
                                    { value: "20", label: "20" },
                                    { value: "50", label: "50" },
                                    { value: "100", label: "100" },
                                    { value: "all", label: "Alle" }
                                  ]}
                                  placeholder="10"
                                  openUpward={true}
                                  whiteBackground={true}
                                  disabled={showConfirmation || selectedSession}
                                />
                              </div>
                              <span className="text-sm text-slate-600 dark:text-slate-400">
                                Einträge pro Seite
                              </span>
                            </div>

                            {/* Pagination Info and Controls */}
                            <div className="flex items-center gap-4">
                              {/* Info Text */}
                              <span className="text-sm text-slate-600 dark:text-slate-400">
                                Zeige <span className="font-semibold text-slate-900 dark:text-slate-100">{startIndex + 1}</span>-<span className="font-semibold text-slate-900 dark:text-slate-100">{Math.min(endIndex, totalItems)}</span> von <span className="font-semibold text-slate-900 dark:text-slate-100">{totalItems}</span> Sessions
                              </span>

                              {/* Scroll to Top Button */}
                              <button
                                onClick={scrollToTop}
                                className="inline-flex items-center justify-center w-9 h-9 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                                title="Nach oben scrollen"
                              >
                                <i className="fas fa-arrow-up text-xs"></i>
                              </button>

                              {/* Pagination Buttons */}
                              {itemsPerPage !== "all" && totalPages > 1 && (
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    className="inline-flex items-center justify-center w-9 h-9 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    <i className="fas fa-chevron-left text-xs"></i>
                                  </button>

                                  {/* Page Numbers */}
                                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                    let pageNum;
                                    if (totalPages <= 5) {
                                      pageNum = i + 1;
                                    } else if (currentPage <= 3) {
                                      pageNum = i + 1;
                                    } else if (currentPage >= totalPages - 2) {
                                      pageNum = totalPages - 4 + i;
                                    } else {
                                      pageNum = currentPage - 2 + i;
                                    }

                                    return (
                                      <button
                                        key={pageNum}
                                        onClick={() => handlePageChange(pageNum)}
                                        className={`inline-flex items-center justify-center w-9 h-9 rounded-xl border transition-colors ${
                                          currentPage === pageNum
                                            ? 'bg-primary dark:bg-primary-light text-white border-primary/30 dark:border-primary-light/30'
                                            : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700'
                                        }`}
                                      >
                                        {pageNum}
                                      </button>
                                    );
                                  })}

                                  <button
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                    className="inline-flex items-center justify-center w-9 h-9 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    <i className="fas fa-chevron-right text-xs"></i>
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Empty State */}
          {filteredSessions.length === 0 && (
            <div className="text-center py-16">
              <div className="w-16 h-16 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="fas fa-chart-bar text-slate-400 dark:text-slate-500 text-xl"></i>
              </div>
              <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100 mb-2">Keine Sessions gefunden</h3>
              <p className="text-slate-500 dark:text-slate-400 mb-6 max-w-md mx-auto">
                {searchTerm || selectedRole !== "all" || selectedStatus !== "all"
                  ? "Versuchen Sie, Ihre Filter zu ändern oder den Suchbegriff anzupassen."
                  : "Es sind noch keine Sessions vorhanden."
                }
              </p>
            </div>
          )}

          {/* Session-Detail Modal */}
          {selectedSession && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
              <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden border border-slate-200 dark:border-slate-700">
                {/* Header */}
                <div className="p-6 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-2">
                        Session-Details
                      </h2>
                      <p className="text-sm text-slate-600 dark:text-slate-400">
                        {selectedSession.session?.user?.username || selectedSession.session?.participantCode || "Anonym"} • {selectedSession.session?.roleName} • Sitzungs-ID: #{selectedSession.session?.id || 'N/A'}
                      </p>
                    </div>
                    <div className="flex items-center gap-4">
                      {/* Status */}
                      <div className="flex flex-col items-end">
                        <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-1">
                          STATUS
                        </span>
                        <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium ${
                          selectedSession.session?.status === "completed"
                            ? "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border border-emerald-700/30 dark:border-emerald-300/30"
                            : selectedSession.session?.status === "in_progress"
                            ? "bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 border border-yellow-700/30 dark:border-yellow-300/30"
                            : "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 border border-red-700/30 dark:border-red-300/30"
                        }`}>
                          <span className={`w-2 h-2 rounded-full mr-2 ${
                            selectedSession.session?.status === "completed"
                              ? "bg-emerald-500"
                              : selectedSession.session?.status === "in_progress"
                              ? "bg-yellow-500"
                              : "bg-red-500"
                          }`}></span>
                          {selectedSession.session?.status === "completed" ? "Abgeschlossen" :
                           selectedSession.session?.status === "in_progress" ? "In Bearbeitung" :
                           "Abgebrochen"}
                        </span>
                      </div>
                      <button
                        onClick={() => setSelectedSession(null)}
                        className="p-2 text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 rounded-md transition-colors"
                      >
                        <i className="fas fa-times text-lg"></i>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)] space-y-6 bg-slate-50/50 dark:bg-slate-900">
                  {/* Aufgaben-Ergebnisse */}
                  {selectedSession.taskResults && selectedSession.taskResults.length > 0 && (() => {
                    // Berechne Gesamtdauer und Erfolgsquote
                    const totalDurationSeconds = selectedSession.taskResults.reduce((sum, result) => sum + (result.durationSeconds || 0), 0);
                    const totalMinutes = Math.floor(totalDurationSeconds / 60);
                    const totalSeconds = totalDurationSeconds % 60;
                    const totalDurationFormatted = `${totalMinutes}:${String(totalSeconds).padStart(2, '0')} Min`;
                    
                    const correctCount = selectedSession.taskResults.filter(r => r.isCorrect).length;
                    const totalCount = selectedSession.taskResults.length;
                    const successRate = totalCount > 0 ? Math.round((correctCount / totalCount) * 100) : 0;
                    
                    return (
                    <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
                      <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-700/50">
                        <div className="flex items-center justify-between">
                          <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide flex items-center gap-2">
                            <i className="fas fa-check-square text-slate-500 dark:text-slate-400"></i>
                            AUFGABEN-ERGEBNISSE
                          </h3>
                          <div className="flex items-center gap-6">
                            {/* Gesamtdauer */}
                            <div className="flex items-center gap-2">
                              <i className="fas fa-clock text-slate-500 dark:text-slate-400 text-sm"></i>
                              <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                {totalDurationFormatted}
                              </span>
                            </div>
                            {/* Erfolgsquote */}
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                {successRate}%
                              </span>
                              <span className="text-sm text-slate-500 dark:text-slate-400">
                                ({correctCount}/{totalCount})
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full divide-y divide-slate-200 dark:divide-slate-700">
                          <thead className="bg-slate-50 dark:bg-slate-700/50">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                                AUFGABE
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                                DAUER
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                                EINGABE & ERGEBNIS
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                                STATUS
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white dark:bg-slate-800 divide-y divide-slate-200 dark:divide-slate-700">
                            {selectedSession.taskResults.map((result, index) => {
                              const durationSeconds = result.durationSeconds || 0;
                              const minutes = Math.floor(durationSeconds / 60);
                              const seconds = durationSeconds % 60;
                              const durationFormatted = `${minutes}:${String(seconds).padStart(2, '0')} Min`;
                              
                              // Parse actualInputValue für bessere Darstellung
                              // Nur bei INPUT_VALUE-Aufgaben, nicht bei STATE_CHANGE
                              let inputDisplay = [];
                              let expectedDisplay = [];
                              
                              // Nur Eingaben anzeigen, wenn es eine INPUT_VALUE-Aufgabe ist
                              const isInputValueTask = result.task?.expectedType === 'INPUT_VALUE';
                              
                              try {
                                if (isInputValueTask && result.actualInputValue) {
                                  // Versuche zuerst als JSON zu parsen
                                  let parsed;
                                  if (typeof result.actualInputValue === 'string') {
                                    try {
                                      parsed = JSON.parse(result.actualInputValue);
                                    } catch {
                                      // Wenn kein JSON, verwende direkt den String
                                      inputDisplay.push({ title: 'Eingabe', value: result.actualInputValue });
                                    }
                                  } else {
                                    parsed = result.actualInputValue;
                                  }
                                  
                                  if (parsed && typeof parsed === 'object' && parsed !== null) {
                                    // Wenn es ein Objekt ist, iteriere über die Eigenschaften
                                    if (Array.isArray(parsed)) {
                                      // Wenn es ein Array ist, iteriere über die Elemente
                                      parsed.forEach((item, idx) => {
                                        if (item && typeof item === 'object') {
                                          // Wenn das Element ein Objekt mit title und value ist
                                          if (item.title !== undefined && item.value !== undefined) {
                                            inputDisplay.push({ title: item.title || 'Eingabe', value: item.value });
                                          } else {
                                            // Sonst zeige alle Eigenschaften
                                            Object.entries(item).forEach(([k, v]) => {
                                              inputDisplay.push({ title: k, value: v });
                                            });
                                          }
                                        } else {
                                          inputDisplay.push({ title: `Eingabe ${idx + 1}`, value: item });
                                        }
                                      });
                                    } else {
                                      // Einzelnes Objekt - prüfe ob es title/value Struktur hat
                                      if (parsed.title !== undefined && parsed.value !== undefined) {
                                        // Objekt mit title und value (einzelnes Eingabefeld mit Titel)
                                        inputDisplay.push({ title: parsed.title || 'Eingabe', value: parsed.value });
                                      } else {
                                        // Wenn es ein Objekt ist, suche nach title/value Struktur
                                        Object.entries(parsed).forEach(([key, value]) => {
                                          if (value !== null && value !== undefined) {
                                            // Wenn der Wert selbst ein Objekt mit title und value ist
                                            if (typeof value === 'object' && !Array.isArray(value) && value.title && value.value !== undefined) {
                                              inputDisplay.push({ title: value.title, value: value.value });
                                            } else if (typeof value === 'object' && !Array.isArray(value)) {
                                              // Wenn es ein Objekt ist, versuche title/value zu finden
                                              if (value.title && value.value !== undefined) {
                                                inputDisplay.push({ title: value.title, value: value.value });
                                              } else {
                                                // Sonst zeige alle Eigenschaften
                                                const valueStr = Object.entries(value)
                                                  .map(([k, v]) => `${k}: ${v}`)
                                                  .join(', ');
                                                inputDisplay.push({ title: key, value: valueStr });
                                              }
                                            } else if (Array.isArray(value)) {
                                              inputDisplay.push({ title: key, value: value.join(', ') });
                                            } else {
                                              inputDisplay.push({ title: key, value: value });
                                            }
                                          }
                                        });
                                      }
                                    }
                                  } else if (parsed !== undefined && parsed !== null) {
                                    inputDisplay.push({ title: 'Eingabe', value: String(parsed) });
                                  }
                                }
                              } catch (e) {
                                // Fallback: Zeige den rohen Wert
                                inputDisplay = [{ title: 'Eingabe', value: String(result.actualInputValue || '-') }];
                              }
                              
                              // Wenn nichts gefunden wurde, zeige einen Standardwert (nur bei INPUT_VALUE)
                              if (isInputValueTask && inputDisplay.length === 0) {
                                inputDisplay = [{ title: 'Eingabe', value: '-' }];
                              }
                              
                              // Parse expectedInputValue für erwartete Werte (wenn vorhanden und INPUT_VALUE)
                              if (isInputValueTask && result.task?.expectedInputValue) {
                                try {
                                  let expectedParsed;
                                  if (typeof result.task.expectedInputValue === 'string') {
                                    // Versuche als JSON zu parsen
                                    try {
                                      expectedParsed = JSON.parse(result.task.expectedInputValue);
                                    } catch {
                                      // Wenn kein JSON, verwende direkt den String
                                      expectedParsed = result.task.expectedInputValue;
                                    }
                                  } else {
                                    expectedParsed = result.task.expectedInputValue;
                                  }
                                  
                                  if (Array.isArray(expectedParsed)) {
                                    // Array von Inputs mit title und value
                                    expectedParsed.forEach((item) => {
                                      if (item && typeof item === 'object') {
                                        if (item.title !== undefined && item.value !== undefined) {
                                          expectedDisplay.push({ title: item.title || 'Erwartet', value: item.value });
                                        } else {
                                          // Wenn kein title/value Format, zeige alle Eigenschaften
                                          Object.entries(item).forEach(([k, v]) => {
                                            expectedDisplay.push({ title: k, value: v });
                                          });
                                        }
                                      } else {
                                        expectedDisplay.push({ title: 'Erwartet', value: String(item) });
                                      }
                                    });
                                  } else if (typeof expectedParsed === 'object' && expectedParsed !== null) {
                                    // Objekt - prüfe ob es title/value Struktur hat
                                    if (expectedParsed.title !== undefined && expectedParsed.value !== undefined) {
                                      expectedDisplay.push({ title: expectedParsed.title || 'Erwartet', value: expectedParsed.value });
                                    } else {
                                      // Iteriere über alle Eigenschaften
                                      Object.entries(expectedParsed).forEach(([key, value]) => {
                                        if (value && typeof value === 'object' && !Array.isArray(value)) {
                                          if (value.title !== undefined && value.value !== undefined) {
                                            expectedDisplay.push({ title: value.title || key, value: value.value });
                                          } else {
                                            expectedDisplay.push({ title: key, value: String(value) });
                                          }
                                        } else {
                                          expectedDisplay.push({ title: key, value: String(value) });
                                        }
                                      });
                                    }
                                  } else {
                                    // Einfacher Wert (String, Number, etc.)
                                    expectedDisplay.push({ title: 'Erwartet', value: String(expectedParsed) });
                                  }
                                } catch (e) {
                                  console.error('Fehler beim Parsen von expectedInputValue:', e, result.task.expectedInputValue);
                                  expectedDisplay.push({ title: 'Erwartet', value: String(result.task.expectedInputValue) });
                                }
                              }
                              
                              return (
                                <tr key={result.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-700/50 transition-colors">
                                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900 dark:text-slate-100">
                                    {index + 1}. {result.task?.title || "Unbekannte Aufgabe"}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600 dark:text-slate-400">
                                    {durationFormatted}
                                  </td>
                                  <td className="px-6 py-4">
                                    <div className="space-y-2">
                                      {/* Eingabewerte mit Referenz zu erwarteten Werten - nur bei INPUT_VALUE */}
                                      {isInputValueTask && (
                                      <div className="flex flex-wrap gap-2">
                                        {inputDisplay.map((item, idx) => {
                                          // Finde den entsprechenden erwarteten Wert (gleicher Index oder gleicher Titel)
                                          const expectedItem = expectedDisplay.length > 0 
                                            ? (expectedDisplay[idx] || expectedDisplay.find(e => e.title === item.title) || expectedDisplay[0])
                                            : null;
                                          
                                          return (
                                            <div key={idx} className="flex flex-col gap-1.5">
                                              <div className="flex items-center gap-2">
                                                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 border border-slate-300 dark:border-slate-600">
                                                  <span className="font-semibold mr-1">{item.title}:</span>
                                                  <span>{item.value}</span>
                                                </span>
                                                {expectedItem && (
                                                  <>
                                                    <i className="fas fa-arrow-right text-xs text-slate-400 dark:text-slate-500"></i>
                                                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800">
                                                      <span className="font-semibold mr-1">{expectedItem.title}:</span>
                                                      <span>{expectedItem.value}</span>
                                                    </span>
                                                  </>
                                                )}
                                              </div>
                                            </div>
                                          );
                                        })}
                                      </div>
                                      )}
                                      {/* Bei STATE_CHANGE: Zeige Info über State-Validierung */}
                                      {!isInputValueTask && (
                                        <div className="text-sm text-slate-600 dark:text-slate-400 italic">
                                          State-Validierung: {result.isCorrect ? 'Erfolgreich' : 'Fehlgeschlagen'}
                                        </div>
                                      )}
                                      {/* Erwartete Werte ohne zugehörige Eingabe (falls vorhanden) - nur bei INPUT_VALUE */}
                                      {isInputValueTask && expectedDisplay.length > inputDisplay.length && (
                                        <div className="flex flex-wrap gap-2 mt-2 pt-2 border-t border-slate-200 dark:border-slate-700">
                                          <span className="text-xs text-slate-500 dark:text-slate-400 font-medium">Weitere erwartete Werte:</span>
                                          {expectedDisplay.slice(inputDisplay.length).map((item, idx) => (
                                            <span key={`extra-${idx}`} className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800">
                                              <span className="font-semibold mr-1">{item.title}:</span>
                                              <span>{item.value}</span>
                                            </span>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center gap-2">
                                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                                        result.isCorrect
                                          ? "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border border-emerald-700/30 dark:border-emerald-300/30"
                                          : "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 border border-red-700/30 dark:border-red-300/30"
                                      }`}>
                                        <i className={`fas ${result.isCorrect ? 'fa-check' : 'fa-times'} mr-1.5 text-[10px]`}></i>
                                        {result.isCorrect ? "Korrekt" : "Falsch"}
                                      </span>
                                      {!result.isCorrect && (
                                        <button
                                          onClick={() => handleMarkAsCorrect(result.id)}
                                          className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium text-primary dark:text-primary-light hover:bg-primary/10 dark:hover:bg-primary-light/20 transition-colors"
                                          title="Als korrekt markieren"
                                        >
                                          <i className="fas fa-check-circle text-xs mr-1"></i>
                                          Korrigieren
                                        </button>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                    );
                  })()}

                  {/* SUS-Antworten */}
                  {selectedSession.susAnswers && selectedSession.susAnswers.length > 0 && (
                    <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
                      <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-700/50">
                         <div className="flex items-center justify-between">
                           <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide flex items-center gap-2">
                             <i className="fas fa-list text-slate-500 dark:text-slate-400"></i>
                             SUS-ANTWORTEN (SYSTEM USABILITY SCALE)
                           </h3>
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">
                                SUS-SCORE:
                              </span>
                              <span className="text-sm font-semibold text-primary dark:text-primary-light">
                                {selectedSession.susScore ? `${parseFloat(selectedSession.susScore).toFixed(1)}/100` : "N/A"}
                              </span>
                            </div>
                         </div>
                      </div>
                      <div className="p-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {selectedSession.susAnswers
                            .sort((a, b) => a.questionIndex - b.questionIndex)
                            .map((answer) => {
                              const score = answer.score || 0;
                              const isPositive = answer.questionIndex % 2 === 1; // Ungerade Fragen sind positiv
                              
                              // Frage-Titel basierend auf Index
                              const questionLabels = {
                                1: "Integration",
                                2: "Komplexität",
                                3: "Einfachheit",
                                4: "Unterstützung",
                                5: "Integration",
                                6: "Inkonsistenz",
                                7: "Erlernbarkeit",
                                8: "Kompliziertheit",
                                9: "Selbstvertrauen",
                                10: "Lernaufwand"
                              };
                              
                              return (
                                <div key={answer.id} className="flex items-center gap-3">
                                  <span className="text-sm font-medium text-slate-700 dark:text-slate-300 min-w-[160px]">
                                    Frage {answer.questionIndex}: {questionLabels[answer.questionIndex] || ''}
                                  </span>
                                  <div className="flex items-center gap-2">
                                    {[1, 2, 3, 4, 5].map((point) => (
                                      <div
                                        key={point}
                                        className={`w-4 h-4 rounded-full transition-colors ${
                                          point <= score
                                            ? isPositive
                                              ? 'bg-primary dark:bg-primary-light'
                                              : 'bg-red-500 dark:bg-red-600'
                                            : 'bg-slate-200 dark:bg-slate-600'
                                        }`}
                                      />
                                    ))}
                                  </div>
                                </div>
                              );
                            })}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Offene Fragen & Feedback */}
                  {selectedSession.openAnswers && selectedSession.openAnswers.length > 0 && (
                    <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
                      <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-700/50">
                        <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide flex items-center gap-2">
                          <i className="fas fa-comment-dots text-slate-500 dark:text-slate-400"></i>
                          OFFENE FRAGEN & FEEDBACK
                        </h3>
                      </div>
                      <div className="p-6 space-y-4">
                        {selectedSession.openAnswers.map((answer) => (
                          <div key={answer.id} className="space-y-2">
                            <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                              {answer.openQuestion?.questionText || "Frage"}
                            </h4>
                            <div className="bg-slate-50 dark:bg-slate-700/50 rounded-xl p-4 border border-slate-200 dark:border-slate-600">
                              <p className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-line">
                                {answer.answerText || "-"}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Footer Actions */}
                <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 flex items-center justify-start">
                  <div className="flex items-center gap-3">
                    <button className="inline-flex items-center gap-2 px-4 py-2 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 text-sm font-medium rounded-lg border border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors">
                      <i className="fas fa-print text-xs"></i>
                      Bericht drucken
                    </button>
                    <button className="inline-flex items-center gap-2 px-4 py-2 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 text-sm font-medium rounded-lg border border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors">
                      <i className="fas fa-download text-xs"></i>
                      PDF Export
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Confirmation Modal */}
          <ConfirmationModal
            isOpen={showConfirmation}
            onClose={handleCloseConfirmation}
            onConfirm={handleConfirmDelete}
            title={selectedSessionIds.size > 0 ? "Sessions löschen" : "Session löschen"}
            subtitle="Diese Aktion kann nicht rückgängig gemacht werden."
            message={selectedSessionIds.size > 0
              ? `Möchten Sie wirklich ${selectedSessionIds.size} ${selectedSessionIds.size === 1 ? 'Session' : 'Sessions'} löschen?`
              : sessionToDelete 
                ? `Möchten Sie die Session #${sessionToDelete.id} wirklich löschen?`
                : "Möchten Sie diese Session wirklich löschen?"}
            confirmText="Löschen"
            cancelText="Abbrechen"
            type="danger"
            layout="inline"
          />

          {/* Mark as Correct Dialog */}
          <ConfirmationModal
            isOpen={showMarkCorrectDialog}
            onClose={handleCloseMarkCorrectDialog}
            onConfirm={handleConfirmMarkAsCorrect}
            title="Als korrekt markieren"
            subtitle="Diese Aufgabe wird dann als korrekt gewertet."
            message={taskResultToMark 
              ? `Möchten Sie die Aufgabe "${taskResultToMark.task?.title || 'Unbekannt'}" wirklich als korrekt markieren?`
              : "Möchten Sie diese Aufgabe wirklich als korrekt markieren?"}
            confirmText="Als korrekt markieren"
            cancelText="Abbrechen"
            type="info"
            layout="inline"
          />

          {/* Import Confirmation Modal */}
          <ConfirmationModal
            isOpen={showImportConfirmation}
            onClose={() => {
              setShowImportConfirmation(false);
              setImportData(null);
            }}
            onConfirm={handleConfirmImport}
            title="Sessions importieren"
            message={importData ? `Möchten Sie ${importData.length} Session(s) importieren? Die Sessions werden als neue Einträge angelegt.` : "Möchten Sie die Sessions importieren?"}
            confirmText="Importieren"
            cancelText="Abbrechen"
            type="info"
          />

          {/* Toast */}
          {toast.show && (
            <div className="fixed top-4 right-4 z-50">
              <div className={`px-4 py-3 rounded-lg border shadow-lg bg-white dark:bg-slate-800 ${
                toast.type === 'success' ? 'border-green-200 dark:border-green-800' :
                toast.type === 'error' ? 'border-red-200 dark:border-red-800' :
                'border-blue-200 dark:border-blue-800'
              }`}>
                <div className="flex items-center gap-3">
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                    toast.type === 'success' ? 'bg-green-100 dark:bg-green-900/30' :
                    toast.type === 'error' ? 'bg-red-100 dark:bg-red-900/30' :
                    'bg-blue-100 dark:bg-blue-900/30'
                  }`}>
                    <i className={`fas text-xs ${
                      toast.type === 'success' ? 'fa-check text-green-600 dark:text-green-400' :
                      toast.type === 'error' ? 'fa-times text-red-600 dark:text-red-400' :
                      'fa-info text-blue-600 dark:text-blue-400'
                    }`}></i>
                  </div>
                  <span className={`text-sm font-medium ${
                    toast.type === 'success' ? 'text-green-900 dark:text-green-100' :
                    toast.type === 'error' ? 'text-red-900 dark:text-red-100' :
                    'text-blue-900 dark:text-blue-100'
                  }`}>{toast.message}</span>
                  <button
                    onClick={hideToast}
                    className="ml-2 text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                  >
                    <i className="fas fa-times text-xs"></i>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

export default UsabilityEvaluationsTailwind;
